import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { physicalAIIntelligenceLinks } from "../../data/physicalAIIntelligence";

function textValue(value) {
  if (value === null || value === undefined || value === "") return "Not disclosed";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

function filterValues(records, key) {
  return [...new Set(records.map((record) => textValue(record[key])).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, "en", { numeric: true }));
}

function EvidenceBadge({ children }) {
  return (
    <span className="inline-flex border border-cyan-300/25 bg-cyan-300/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.13em] text-cyan-100">
      {children || "Source-backed"}
    </span>
  );
}

function SourceCell({ record }) {
  if (!record.sourceUrl) return <span className="text-slate-500">Source not retained</span>;
  return (
    <><a
      href={record.sourceUrl}
      target="_blank"
      rel="noreferrer"
      className="font-bold text-cyan-300 underline decoration-cyan-300/40 underline-offset-4 hover:text-cyan-100"
    >
      {record.sourceLabel || "Open source"}
    </a>{(record.additionalSources || []).map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer" className="mt-2 block font-bold text-cyan-300 underline underline-offset-4">{source.label}</a>)}</>
  );
}

function MobileRecord({ record, columns }) {
  return (
    <article className="border border-white/10 bg-[#07101f]/90 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-black tracking-tight text-white">
          {textValue(record.model || record.dataset || record.benchmark || record.robot || record.id)}
        </h3>
        {record.evidence && <EvidenceBadge>{record.evidence}</EvidenceBadge>}
      </div>
      <dl className="mt-4 grid gap-px bg-white/10">
        {columns.map(([key, label]) => (
          <div key={key} className="bg-[#030712] px-3 py-3">
            <dt className="text-[10px] font-black uppercase tracking-[0.13em] text-slate-500">{label}</dt>
            <dd className="mt-1 break-words text-sm leading-6 text-slate-200">{textValue(record[key])}</dd>
          </div>
        ))}
        <div className="bg-[#030712] px-3 py-3">
          <dt className="text-[10px] font-black uppercase tracking-[0.13em] text-slate-500">Source</dt>
          <dd className="mt-1 break-words text-sm leading-6"><SourceCell record={record} /></dd>
        </div>
      </dl>
    </article>
  );
}

export default function PhysicalAIDatabasePanel({ page, embedded = false }) {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({});
  const records = page?.records || [];
  const columns = page?.columns || [];
  const filterConfig = page?.filters || [];

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return records.filter((record) => {
      if (normalizedQuery) {
        const searchable = Object.values(record)
          .filter((value) => ["string", "number", "boolean"].includes(typeof value))
          .join(" ")
          .toLowerCase();
        if (!searchable.includes(normalizedQuery)) return false;
      }
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        return textValue(record[key]) === value;
      });
    });
  }, [records, query, filters]);

  const clearFilters = () => {
    setQuery("");
    setFilters({});
  };

  return (
    <section className={embedded ? "mt-8 overflow-hidden border border-slate-800 bg-[#030712] text-slate-100" : "text-slate-100"} aria-label={`${page.title} database`}>
      {embedded && (
        <header className="border-b border-white/10 px-5 py-6 sm:px-7">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-cyan-300">{page.eyebrow}</p>
          <h2 className="mt-3 text-2xl font-black tracking-tight text-white sm:text-3xl">{page.title}</h2>
          <p className="mt-3 max-w-4xl text-sm leading-7 text-slate-300">{page.description}</p>
          <div className="mt-4 flex flex-wrap gap-3 text-xs font-bold text-slate-500">
            <span>{records.length} retained records</span>
            <span>Recorded source dates appear in the data; this is not a live feed.</span>
          </div>
        </header>
      )}

      <div className={embedded ? "p-5 sm:p-7" : ""}>
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_repeat(2,minmax(170px,0.28fr))_auto]">
          <label className="block">
            <span className="sr-only">Search {page.title}</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search model, company, robot, task or evidence…"
              className="h-11 w-full border border-white/15 bg-white/[0.045] px-3 text-sm font-semibold text-white outline-none placeholder:text-slate-600 focus:border-cyan-300/60"
            />
          </label>
          {filterConfig.slice(0, 2).map(([key, label]) => (
            <label key={key} className="block">
              <span className="sr-only">Filter by {label}</span>
              <select
                value={filters[key] || ""}
                onChange={(event) => setFilters((current) => ({ ...current, [key]: event.target.value }))}
                className="h-11 w-full border border-white/15 bg-[#07101f] px-3 text-sm font-bold text-slate-200 outline-none focus:border-cyan-300/60"
              >
                <option value="">All {label}</option>
                {filterValues(records, key).map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
          ))}
          <button
            type="button"
            onClick={clearFilters}
            className="h-11 border border-white/15 px-4 text-xs font-black uppercase tracking-[0.12em] text-slate-300 transition hover:border-cyan-300/40 hover:text-white"
          >
            Reset
          </button>
        </div>

        {filterConfig.length > 2 && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {filterConfig.slice(2).map(([key, label]) => (
              <label key={key} className="block">
                <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.13em] text-slate-500">{label}</span>
                <select
                  value={filters[key] || ""}
                  onChange={(event) => setFilters((current) => ({ ...current, [key]: event.target.value }))}
                  className="h-10 w-full border border-white/15 bg-[#07101f] px-3 text-xs font-bold text-slate-200 outline-none focus:border-cyan-300/60"
                >
                  <option value="">All</option>
                  {filterValues(records, key).map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </label>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between border-y border-white/10 py-3 text-xs font-bold text-slate-500">
          <span>{filtered.length} of {records.length} records shown</span>
          <span>Filters are local and do not create crawlable URLs</span>
        </div>

        <div className="mt-4 grid gap-3 md:hidden">
          {filtered.map((record) => <MobileRecord key={record.id} record={record} columns={columns} />)}
        </div>

        <div className="mt-4 hidden overflow-x-auto border border-white/10 md:block">
          <table className="w-full min-w-[1180px] border-collapse text-left text-xs">
            <thead className="sticky top-0 z-10 bg-[#0a1322] text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">
              <tr>
                {columns.map(([, label]) => <th key={label} scope="col" className="whitespace-nowrap px-3 py-3">{label}</th>)}
                <th scope="col" className="px-3 py-3">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 bg-[#07101f]/90">
              {filtered.map((record) => (
                <tr key={record.id} className="align-top hover:bg-white/[0.025]">
                  {columns.map(([key]) => (
                    <td key={key} className="max-w-[280px] px-3 py-4 leading-5 text-slate-300">
                      <span className={key === "model" || key === "dataset" || key === "benchmark" ? "font-black text-white" : ""}>{textValue(record[key])}</span>
                    </td>
                  ))}
                  <td className="max-w-[220px] px-3 py-4 leading-5"><SourceCell record={record} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className="mt-4 border border-white/10 bg-white/[0.035] p-6 text-sm text-slate-400">
            No retained record matches these filters.
          </div>
        )}

        <div className="mt-6 border border-white/10 bg-white/[0.035] p-5">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-violet-300">Methodology</p>
          <p className="mt-3 max-w-5xl text-sm leading-7 text-slate-300">{page.methodology}</p>
        </div>

        <nav className="mt-6" aria-label="Physical AI intelligence databases">
          <p className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-500">Connected Physical AI databases</p>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
            {physicalAIIntelligenceLinks.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`shrink-0 border px-3 py-2 text-xs font-black transition ${item.path === page.path ? "border-cyan-300/50 bg-cyan-300/10 text-cyan-100" : "border-white/10 bg-white/[0.035] text-slate-300 hover:border-white/25 hover:text-white"}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </section>
  );
}
